<script lang="ts" setup>
  import AiChatSidebar from '@/components/app/cases/chat/AiChatSidebar.vue'
  import AiChatMain from '@/components/app/cases/chat/AiChatMain.vue'
  import AiChatDebugPanel from '@/components/app/cases/chat/AiChatDebugPanel.vue'
  import PDFViewer from '@/components/features/pdf/pdfViewer.vue'
  import { computed, onMounted, type PropType, provide, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import {
    type AIChatSession,
    AiChatSessionsService,
    DocumentsService,
    type CustomerCase,
    type AIChatSessionMessage,
    type CustomerUser as User,
    type CustomerCase as Case
  } from '@/types/generated'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import EntityCard from '@/components/common/EntityCard.vue'
  const caseStore = useCaseStore()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const currentUser = computed<User>(() => useUserStore().user as User)
  provide('currentUser', currentUser)

  provide('caseData', caseRecord)

  const lastResponseTime = ref<number | null>(null)
  provide('lastResponseTime', lastResponseTime)

  const activeChatSession = ref<AIChatSession | null>(null)
  provide('activeChatSession', activeChatSession)
  const pollIntervals = ref(new Map<string, number>())
  const generatingSessionUuids = ref(new Set<string>())

  const pollChatSession = async (session: AIChatSession) => {
    const updatedSession = await AiChatSessionsService.aiChatSessionsRetrieve({
      uuid: session.uuid
    })
    console.log('session state', updatedSession.session_state)

    // Update session state in a reactive way
    if (activeChatSession.value && activeChatSession.value.uuid === session.uuid) {
      activeChatSession.value = {
        ...activeChatSession.value,
        session_state: updatedSession.session_state
      }
    }

    // Also update in the chat_sessions array to keep everything in sync
    const sessionIndex = caseRecord.value.chat_sessions.findIndex((s) => s.uuid === session.uuid)
    if (sessionIndex !== -1) {
      caseRecord.value.chat_sessions[sessionIndex] = {
        ...caseRecord.value.chat_sessions[sessionIndex],
        session_state: updatedSession.session_state
      }
    }
  }

  const stopPolling = (sessionUuid: string) => {
    const interval = pollIntervals.value.get(sessionUuid)
    if (interval) {
      window.clearInterval(interval)
      pollIntervals.value.delete(sessionUuid)
    }
  }

  // Watch for active session changes and fetch full session data
  watch(
    () => activeChatSession.value?.uuid,
    async (newUuid) => {
      if (newUuid && isDebugMode.value) {
        console.log('Fetching full session for', newUuid)
        const fullSession = await AiChatSessionsService.aiChatSessionsRetrieve({
          uuid: newUuid
        })
        console.log('Got full session:', fullSession)

        // Update active chat session with spread to ensure reactivity
        activeChatSession.value = { ...fullSession }

        // Update in the chat_sessions array
        const sessionIndex = caseRecord.value.chat_sessions.findIndex((s) => s.uuid === newUuid)
        if (sessionIndex !== -1) {
          caseRecord.value.chat_sessions[sessionIndex] = { ...fullSession }
        }
      }
    }
  )

  const changeActiveChatSession = (chatSession: AIChatSession) => {
    console.log('Changing active session to:', chatSession)
    activeChatSession.value = { ...chatSession }
  }
  provide('changeActiveChatSession', changeActiveChatSession)
  const createNewChatSession = async () => {
    const session = await AiChatSessionsService.aiChatSessionsCreate({
      requestBody: {
        case_uuid: caseRecord.value.uuid as string
      }
    })
    activeChatSession.value = session
    caseRecord.value.chat_sessions.unshift(session)
    return session
  }
  provide('createNewChatSession', createNewChatSession)

  const isSessionGenerating = (sessionUuid: string) => {
    return generatingSessionUuids.value.has(sessionUuid)
  }
  const isAnySessionGenerating = computed(() => generatingSessionUuids.value.size > 0)

  provide('generatingSessionUuids', generatingSessionUuids)
  provide('isSessionGenerating', isSessionGenerating)
  provide('isAnySessionGenerating', isAnySessionGenerating)

  const sendChatMessage = async (session: AIChatSession | null, message: string) => {
    if (!activeChatSession.value) {
      session = await createNewChatSession()
    }

    // Add session UUID to generating set
    generatingSessionUuids.value.add(session!.uuid)

    debugPanelRef.value?.startResponseTimer()

    // Start polling for this specific session
    const interval = window.setInterval(() => pollChatSession(session!), 1000)
    pollIntervals.value.set(session!.uuid, interval)

    session!.messages.push({
      content: message,
      role: 'user',
      user_uuid: currentUser.value.uuid
    } as AIChatSessionMessage)

    try {
      const result = await AiChatSessionsService.aiChatSessionsSendMessageCreate({
        uuid: session!.uuid,
        requestBody: {
          content: message
        }
      })
      session!.messages.push(result.message as AIChatSessionMessage)
      debugPanelRef.value?.endResponseTimer()
    } finally {
      // Stop polling for this specific session
      stopPolling(session!.uuid)
      // Remove session UUID from generating set
      generatingSessionUuids.value.delete(session!.uuid)
    }
  }
  provide('sendChatMessage', sendChatMessage)

  const isDocumentPanelOpen = ref(false)
  provide('isDocumentPanelOpen', isDocumentPanelOpen)

  // PDF viewer state
  const isPdfOpen = ref(false)
  const pdfUrl = ref('')
  const pdfFilename = ref('')
  const pdfDocument = ref<{ filesize?: number; page_count?: number } | undefined>(undefined)
  provide('isPdfOpen', isPdfOpen)

  const handleDocumentClick = async (document: any) => {
    try {
      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        pdfUrl.value = response.url
        pdfFilename.value = document.title
        // Store document metadata for scanned PDF detection
        pdfDocument.value = {
          filesize: response.filesize,
          page_count: response.page_count
        }
        isPdfOpen.value = true
      }
    } catch (error) {
      console.error('Failed to fetch document:', error)
    }
  }
  provide('handleDocumentClick', handleDocumentClick)

  const toggleDocumentPanel = () => {
    isDocumentPanelOpen.value = !isDocumentPanelOpen.value
  }
  provide('toggleDocumentPanel', toggleDocumentPanel)

  const route = useRoute()
  const isDebugMode = computed(() => route.query.debug === 'true')
  provide('isDebugMode', isDebugMode)

  const debugPanelRef = ref()
  provide('debugPanelRef', debugPanelRef)
</script>
<template>
  <div class="flex h-full w-full">
    <div
      v-if="currentUser"
      class="mt-10 flex h-[calc(100vh-12rem)] flex-1 rounded-lg border-b border-r border-t border-gray-200"
    >
      <AiChatSidebar />
      <AiChatMain />
    </div>
    <AiChatDebugPanel
      ref="debugPanelRef"
      :active-chat-session="activeChatSession"
      :generating-session-uuids="generatingSessionUuids"
      :current-user="currentUser"
    />
    <PDFViewer
      v-model:isOpen="isPdfOpen"
      v-model:url="pdfUrl"
      :filename="pdfFilename"
      :scale="1.0"
      :document="pdfDocument"
    />
  </div>
  <EntityCard />
</template>

<style scoped>
  /* ... */
</style>
